/**
 * CheckYourEmailPage Component
 *
 * Displayed after successful registration form submission.
 * Instructs user to check their email for verification link.
 *
 * Project: P07 - Stripe Integration
 * Sprint: S02 - Registration Flow
 * Work Unit: WU03 - Email Confirmation
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Mail, RefreshCw } from 'lucide-react';

import { Button, Card, CardBody, Link, useToastNotification } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';

// =============================================================================
// API Helper
// =============================================================================

interface ResendResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  retryAfter?: number;
}

async function resendVerificationEmail(email: string): Promise<ResendResponse> {
  const response = await fetch(
    `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/resend-verification`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env['VITE_SUPABASE_ANON_KEY'] as string,
      },
      body: JSON.stringify({ email }),
    }
  );

  const data = await response.json();

  if (response.status === 429) {
    return {
      success: false,
      error: data.error || 'Too many requests',
      code: 'RATE_LIMITED',
      retryAfter: data.retryAfter,
    };
  }

  return data;
}

// =============================================================================
// Types
// =============================================================================

interface LocationState {
  email?: string;
}

// =============================================================================
// Component
// =============================================================================

export function CheckYourEmailPage(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToastNotification();
  const state = location.state as LocationState | null;
  const email = state?.email;

  const [isResending, setIsResending] = useState(false);

  // Redirect to register if no email in state (direct URL access)
  useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  // Handle resend verification email
  const handleResend = useCallback(async () => {
    if (!email || isResending) return;

    setIsResending(true);

    try {
      const result = await resendVerificationEmail(email);

      if (result.success) {
        showToast({
          title: 'Email sent',
          description: 'A new verification link has been sent to your email.',
          color: 'success',
          timeout: 5000,
        });
      } else if (result.code === 'RATE_LIMITED') {
        const retryMinutes = result.retryAfter
          ? Math.ceil(result.retryAfter / 60)
          : 60;
        showToast({
          title: 'Too many requests',
          description: `Please wait ${retryMinutes} minute${retryMinutes > 1 ? 's' : ''} before trying again.`,
          color: 'warning',
          timeout: 5000,
        });
      } else {
        showToast({
          title: 'Failed to send',
          description: result.error || 'Please try again later.',
          color: 'danger',
          timeout: 5000,
        });
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      showToast({
        title: 'Network error',
        description: 'Please check your connection and try again.',
        color: 'danger',
        timeout: 5000,
      });
    } finally {
      setIsResending(false);
    }
  }, [email, isResending, showToast]);

  // Show nothing while redirecting
  if (!email) {
    return <></>;
  }

  return (
    <AppLayout hideFooter={true}>
      <Card
        className="items-center justify-center h-full"
        radius="none"
        shadow="none"
      >
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <div className="text-center space-y-6">
            {/* Mail Icon */}
            <div className="text-primary-500 flex justify-center">
              <Mail className="w-16 h-16" strokeWidth={1.5} />
            </div>

            {/* Heading and Instructions */}
            <div className="space-y-3">
              <h1 className="text-xl font-semibold text-default-900">
                Check your email
              </h1>
              <p className="text-default-600">
                We&apos;ve sent a verification link to{' '}
                <strong className="text-default-900">{email}</strong>.
              </p>
              <p className="text-default-600">
                Click the link in the email to verify your account and continue
                with registration.
              </p>
            </div>

            {/* Spam Folder Reminder + Resend */}
            <div className="bg-default-100 rounded-lg p-4 space-y-3">
              <p className="text-sm text-default-600">
                Don&apos;t see it? Check your spam folder or make sure{' '}
                <strong>{Brand.supportEmail}</strong> is in your contacts.
              </p>
              <Button
                variant="flat"
                size="sm"
                onPress={handleResend}
                isLoading={isResending}
                isDisabled={isResending}
                startContent={!isResending && <RefreshCw className="w-4 h-4" />}
                className="w-full"
              >
                {isResending ? 'Sending...' : 'Resend verification email'}
              </Button>
            </div>

            {/* Actions */}
            <div className="space-y-4 pt-2">
              <RouterLink to="/login">
                <Button color="primary" size="lg" className="w-full">
                  Back to Sign In
                </Button>
              </RouterLink>

              <RouterLink to="/register">
                <Button variant="light" size="sm" className="w-full">
                  Use a different email
                </Button>
              </RouterLink>
            </div>

            {/* Support Contact */}
            <p className="text-xs text-default-400 pt-4">
              Having trouble?{' '}
              <Link
                href={`mailto:${Brand.supportEmail}`}
                size="sm"
                className="text-xs"
              >
                Contact support
              </Link>
            </p>
          </div>
        </CardBody>
      </Card>
    </AppLayout>
  );
}
